<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import BrowseLayout from '@/browse/BrowseLayout.vue'
import BrowsePresentLevelsFilters from '@/browse/presentlevels/BrowsePresentLevelsFilters.vue'
import BrowsePresentLevelsResults from '@/browse/presentlevels/BrowsePresentLevelsResults.vue'
import type { AreaOfNeed, EnrolledGrade } from '@/browse/presentlevels/BrowsePresentLevelsFilters.vue'
import { useBrowseState } from '@/browse/useBrowseState'
import {
  defaultLevelFor,
  firstUnlockedCategory,
  firstUnlockedStatement,
  presentLevelsBrowseConfig,
  selectedCategoryOf,
  selectedGradeOf,
  selectedLevelOf,
  selectedSubjectOf,
  subjectTypeFor,
  withUnlockedCategoriesFirst
} from '@/browse/presentlevels/present_levels_browse'
import type { BrowseStatement } from '@/browse/presentlevels/statement_content'
import { getAreasOfNeed, getEnrolledGradesSet, getSkillBySubject, getStatementsBySkill } from '@/presentlevels/api_v1.js'
import type { Skill } from '@/presentlevels/no-ai/skill_statement_types'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

// Profiles with effect statements cover behavior there, so it is not browsed as its own subject.
const SUBJECTS_COVERED_BY_EFFECTS = ['Behavior']
const INTRO_LINES = [
  "To begin, select your student's grade level.",
  'Then, view present levels statements by subject and category.',
  'Edit each statement to personalize for your student.'
]
const REDIRECT = { replace: true }
const SELECTED_EVENT_BY_KEY: Record<string, string> = {
  grade: EVENT_NAMES.BROWSE_PRESENT_LEVELS.SELECTED_GRADE,
  subject: EVENT_NAMES.BROWSE_PRESENT_LEVELS.SELECTED_SUBJECT,
  category: EVENT_NAMES.BROWSE_PRESENT_LEVELS.SELECTED_CATEGORY,
  level: EVENT_NAMES.BROWSE_PRESENT_LEVELS.SELECTED_LEVEL
}

const route = useRoute()
const userStore = useUserStore()
const { fetching: isFetchingUser, licenseSubType, userContentProfileId } = storeToRefs(userStore)
const { selVisibility, effectsVisibility } = storeToRefs(useVisibilityStore())

const grades: EnrolledGrade[] = getEnrolledGradesSet()
const subjects = computed((): AreaOfNeed[] => getAreasOfNeed({
  selBlocked: !selVisibility.value,
  blockedSubjects: effectsVisibility.value ? SUBJECTS_COVERED_BY_EFFECTS : []
}))

const categoriesBySubject = ref<Record<string, Skill[]>>({})
const statementsByCategoryUuid = ref<Record<string, BrowseStatement[]>>({})
const isFetchingData = ref(false)
const editedStatementTextByLevel = ref<Record<string, string>>({})

const config = presentLevelsBrowseConfig({
  grades: () => grades.map((grade) => grade.val),
  subjects: () => subjects.value.map((subject) => subject.value),
  categoryUuidsFor: (subject) => categoriesBySubject.value[subject]?.map((category) => category.skill_uuid),
  levelLabelsFor: (categoryUuid) => statementsByCategoryUuid.value[categoryUuid]?.map((statement) => statement.level_label)
})

const { state, refine } = useBrowseState(config)

const selectedGrade = computed(() => selectedGradeOf(state.value))
const selectedSubject = computed(() => selectedSubjectOf(state.value))
const selectedCategoryUuid = computed(() => selectedCategoryOf(state.value))
const selectedLevel = computed(() => selectedLevelOf(state.value))

const categories = computed(() => withUnlockedCategoriesFirst(categoriesBySubject.value[selectedSubject.value] ?? []))
const currentCategory = computed(() => categories.value.find((category) => category.skill_uuid === selectedCategoryUuid.value))
const statements = computed(() => statementsByCategoryUuid.value[selectedCategoryUuid.value] ?? [])
const currentStatement = computed(() => statements.value.find((statement) => statement.level_label === selectedLevel.value))
const subjectType = computed(() => subjectTypeFor(selectedSubject.value, statements.value))
// One short line instead of the whole statement, which the results used to re-read on every change.
const resultStatus = computed(() => {
  const category = currentCategory.value
  if (isFetchingUser.value || isFetchingData.value || !category || !selectedLevel.value) {
    return ''
  }
  return `${category.skill_name}, ${selectedLevel.value}`
})

const analytics = getAnalyticsTracker().getChildContext({
  ref: computed(() => route.query.ref),
  browse_pl_subject: selectedSubject,
  browse_pl_filter_grade: selectedGrade,
  browse_pl_filter_category: computed(() => currentCategory.value?.skill_name),
  browse_pl_filter_category_uuid: selectedCategoryUuid,
  browse_pl_statement_level: selectedLevel
})

// A failed request is logged and left uncached, so the page falls back to its intro rather
// than waiting on a spinner that never stops, and a later query change can fetch it again.
async function fetchWhileLoading<T>(request: () => Promise<T | undefined>): Promise<T | undefined> {
  isFetchingData.value = true
  try {
    return await request()
  } catch (e) {
    console.error('Could not load present levels', e)
    return undefined
  } finally {
    isFetchingData.value = false
  }
}

async function loadCategories(subject: string) {
  if (categoriesBySubject.value[subject]) {
    return
  }
  const result = await fetchWhileLoading(() => getSkillBySubject(subject, licenseSubType.value, ''))
  if (!result?.skills) {
    return
  }
  categoriesBySubject.value = { ...categoriesBySubject.value, [subject]: result.skills }
}

function byLevelOrder(a: BrowseStatement, b: BrowseStatement): number {
  return Number(a.level_order) - Number(b.level_order)
}

async function loadStatements(categoryUuid: string) {
  if (statementsByCategoryUuid.value[categoryUuid]) {
    return
  }
  const result = await fetchWhileLoading(() =>
    getStatementsBySkill(categoryUuid, userContentProfileId.value, licenseSubType.value, selectedSubject.value)
  )
  if (!result?.statements) {
    return
  }
  statementsByCategoryUuid.value = { ...statementsByCategoryUuid.value, [categoryUuid]: [...result.statements].sort(byLevelOrder) }
}

function isCategoryUsable(): boolean {
  return Boolean(currentCategory.value && !currentCategory.value.locked)
}

// Drills down one step at a time: each choice that is missing, or locked, is filled with the
// first usable option, which navigates and so brings the page back here for the next step.
// Each redirect lands before this returns, so the page view is reported with what it shows.
async function showStatementForQuery() {
  if (!selectedSubject.value) {
    return
  }
  await loadCategories(selectedSubject.value)
  if (!categories.value.length) {
    return
  }
  if (!isCategoryUsable()) {
    const category = firstUnlockedCategory(categories.value)
    await refine('category', category ? [category.skill_uuid] : [], REDIRECT)
    return
  }
  await loadStatements(selectedCategoryUuid.value)
  if (!statements.value.length) {
    return
  }
  if (!selectedLevel.value) {
    await showDefaultLevel()
    return
  }
  if (currentStatement.value?.locked) {
    await showUnlockedLevel()
    return
  }
  analytics.sendEvent(EVENT_NAMES.BROWSE_PRESENT_LEVELS.VIEWED_STATEMENT)
}

async function showDefaultLevel() {
  const defaultLevel = defaultLevelFor({ subjectType: subjectType.value, statements: statements.value, grade: selectedGrade.value })
  if (defaultLevel) {
    await refine('level', [defaultLevel], REDIRECT)
  }
}

// A trial account cannot open a locked level, so it is moved to the lowest level it can open.
async function showUnlockedLevel() {
  const unlockedStatement = firstUnlockedStatement(statements.value)
  if (!unlockedStatement) {
    return
  }
  await refine('level', [unlockedStatement.level_label], REDIRECT)
  analytics.sendEvent(SELECTED_EVENT_BY_KEY.level)
}

// Reported once the choice has landed, so the event carries the filters it led to.
async function select(key: string, values: string[]) {
  await refine(key, values)
  analytics.sendEvent(SELECTED_EVENT_BY_KEY[key])
}

function selectLevel(level: string) {
  return select('level', [level])
}

// The statements are fetched with the user's license, so nothing loads until the user has.
async function showPage(isFetching: boolean) {
  if (isFetching) {
    return
  }
  await showStatementForQuery()
  analytics.sendEvent(EVENT_NAMES.BROWSE_PRESENT_LEVELS.VIEWED_PAGE)
}

function showStatementForChangedQuery() {
  if (!isFetchingUser.value) {
    showStatementForQuery()
  }
}

watch(isFetchingUser, showPage, { immediate: true })
watch(() => route.query, showStatementForChangedQuery)
watch(statements, () => { editedStatementTextByLevel.value = {} })
</script>

<template>
  <BrowseLayout title="Browse Present Levels" description="Quickly view present levels statements and assessments">
    <template v-if="!isFetchingUser" #filters>
      <BrowsePresentLevelsFilters :state="state" :grades="grades" :subjects="subjects" :categories="categories" @refine="select" />
    </template>

    <p role="status" class="sr-only" data-testid="result-status">{{ resultStatus }}</p>
    <div v-if="isFetchingUser || isFetchingData" class="flex items-center justify-center gap-2 text-text-md text-gray-700">
      <ProgressSpinner class="!m-0 !h-4 !w-4" stroke-width="6" aria-hidden="true" />
      Loading...
    </div>
    <BrowsePresentLevelsResults
      v-else-if="currentCategory && selectedLevel"
      v-model:edited-text-by-level="editedStatementTextByLevel"
      :category="currentCategory"
      :statements="statements"
      :selected-level="selectedLevel"
      :selected-grade="selectedGrade"
      :subject-type="subjectType"
      @select-level="selectLevel"
    />
    <div v-else class="pt-10 text-text-2xl leading-8 text-gray-900" data-testid="browse-present-levels-intro">
      <p v-for="line in INTRO_LINES" :key="line" class="mb-0.5">{{ line }}</p>
    </div>
  </BrowseLayout>
</template>
